import type { CopilotContextRef } from '@fos/shared/contracts/copilot';
import { useLocation } from 'react-router';
import { trpc } from '../../lib/trpc';

/**
 * Kontekst bieżącego ekranu dla Copilota (Fala 10, bąbel AI): profil klienta → „ten klient”, karta zadania → „to
 * zadanie”, projekt, wątek poczty, spotkanie. Rozpoznanie z adresu (bez zmian w widokach modułów); nazwa do nagłówka
 * panelu z danych, które ekran już pobrał (zapytania wyłączone — tylko odczyt pamięci podręcznej, bez dodatkowych
 * żądań). Serwer i tak sprawdza dostęp do rekordu przy każdym pytaniu (`copilot.chat` z `contextRef`).
 */

const ROUTES: readonly { re: RegExp; type: CopilotContextRef['type'] }[] = [
  { re: /^\/klienci\/([0-9a-f-]{36})(?:\/|$)/i, type: 'client' },
  { re: /^\/projekty\/([0-9a-f-]{36})\/?$/i, type: 'project' },
  { re: /^\/zadania\/([0-9a-f-]{36})\/?$/i, type: 'task' },
  { re: /^\/poczta\/([0-9a-f-]{36})\/?$/i, type: 'thread' },
  { re: /^\/spotkania\/([0-9a-f-]{36})\/?$/i, type: 'meeting' },
];

/** Adres → odnośnik kontekstu (null = ekran bez rekordu, np. Dziś, listy). */
export function screenContextRef(pathname: string): CopilotContextRef | null {
  for (const r of ROUTES) {
    const m = r.re.exec(pathname);
    if (m?.[1]) return { type: r.type, id: m[1].toLowerCase() };
  }
  return null;
}

/** Nazwa rodzaju, gdy ekran jeszcze nie ma danych rekordu. */
export const SCREEN_CONTEXT_FALLBACK: Record<CopilotContextRef['type'], string> = {
  client: 'ten klient',
  project: 'ten projekt',
  task: 'to zadanie',
  thread: 'ten wątek',
  message: 'ta wiadomość',
  meeting: 'to spotkanie',
  draft: 'ten szkic',
};

const NONE = '00000000-0000-0000-0000-000000000000';

function text(v: unknown): string | null {
  return typeof v === 'string' && v.trim() ? v.trim() : null;
}

export function useScreenContext(): { ref: CopilotContextRef | null; label: string | null } {
  const { pathname } = useLocation();
  const ref = screenContextRef(pathname);
  const idOf = (type: CopilotContextRef['type']) => (ref?.type === type ? ref.id : NONE);
  const off = { enabled: false } as const;
  const client = trpc.clients.get.useQuery({ id: idOf('client') }, off);
  const project = trpc.projects.get.useQuery({ id: idOf('project') }, off);
  const task = trpc.tasks.get.useQuery({ id: idOf('task') }, off);
  const meeting = trpc.meetings.get.useQuery({ id: idOf('meeting') }, off);
  const thread = trpc.mail.getThread.useQuery({ id: idOf('thread') }, off);
  if (!ref) return { ref: null, label: null };
  let label: string | null = null;
  if (ref.type === 'client') label = text((client.data as { name?: unknown } | undefined)?.name);
  if (ref.type === 'project') label = text((project.data as { name?: unknown } | undefined)?.name);
  if (ref.type === 'task') {
    const t = task.data as { title?: unknown; number?: unknown } | undefined;
    const title = text(t?.title);
    label = title ? (typeof t?.number === 'number' ? `#${t.number} ${title}` : title) : null;
  }
  if (ref.type === 'meeting') label = text((meeting.data as { title?: unknown } | undefined)?.title);
  if (ref.type === 'thread') {
    const d = thread.data as { subject?: unknown; thread?: { subject?: unknown } } | undefined;
    label = text(d?.subject) ?? text(d?.thread?.subject);
  }
  return { ref, label: label ?? SCREEN_CONTEXT_FALLBACK[ref.type] };
}
